// ══════════════════════════════════════════════════════════════════════════════
// SELDOR — MilestoneCard
// Renders a milestone with one of 4 states: completed / ready / in_progress / locked
// Shows consecutive-months streak progress and the blocking reason when locked.
// ══════════════════════════════════════════════════════════════════════════════

import { CheckCircle2, Target, Check, Lock, Flame, Clock } from 'lucide-react';
import { useTheme } from '@contexts/ThemeContext';
import { useLang } from '@contexts/LangContext';
import { Card, Button, Badge } from '@components/ui/Primitives';

export function MilestoneCard({ milestone, onMarkComplete }) {
  const { theme } = useTheme();
  const { t, lang, isRTL } = useLang();

  const gate = milestone.gate || { state: 'ready' };
  const isCompleted = gate.state === 'completed';
  const isLocked = gate.state === 'locked';
  const isInProgress = gate.state === 'in_progress';
  const isReady = gate.state === 'ready';

  const target = parseFloat(milestone.metric_target || 0);
  const current = parseFloat(milestone.metric_current || 0);
  const required = parseInt(milestone.consecutive_months_required || 0);
  const achieved = parseInt(milestone.consecutive_months_achieved || 0);

  // Metric progress percentage
  let metricProgressPct = 0;
  if (target > 0) {
    if (milestone.metric_operator === 'lte') {
      metricProgressPct = current <= target ? 100 : Math.max(0, 100 - ((current - target) / target * 100));
    } else if (milestone.metric_operator === 'eq') {
      metricProgressPct = current === target ? 100 : 0;
    } else {
      metricProgressPct = Math.min((current / target) * 100, 100);
    }
  }

  // Visual styling per state
  const stateStyle = isCompleted ? {
    borderColor: theme.success,
    bg: theme.bgCard,
    opacity: 1,
    icon: <CheckCircle2 size={20} color={theme.success} />
  } : isReady ? {
    borderColor: theme.primary,
    bg: theme.bgCard,
    opacity: 1,
    icon: <Flame size={20} color={theme.primary} />
  } : isInProgress ? {
    borderColor: theme.warning,
    bg: theme.bgCard,
    opacity: 1,
    icon: <Clock size={20} color={theme.warning} />
  } : {
    // locked
    borderColor: theme.textTertiary,
    bg: theme.bgInput,
    opacity: 0.6,
    icon: <Lock size={20} color={theme.textTertiary} />
  };

  return (
    <Card
      style={{
        padding: 20,
        opacity: stateStyle.opacity,
        background: stateStyle.bg,
        [isRTL ? 'borderRight' : 'borderLeft']: `3px solid ${stateStyle.borderColor}`,
        transition: 'all 0.2s'
      }}
      title={isLocked ? (lang === 'ar' ? gate.reason_ar : gate.reason_en) : undefined}
    >
      <div style={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 10,
        marginBottom: 12
      }}>
        <div style={{ flex: 1 }}>
          <div style={{
            fontSize: 14,
            fontWeight: 700,
            color: theme.text,
            marginBottom: 4
          }}>
            {lang === 'ar' ? milestone.title_ar : milestone.title_en}
          </div>
          <div style={{
            fontSize: 12,
            color: theme.textSecondary,
            lineHeight: 1.5
          }}>
            {lang === 'ar' ? milestone.description_ar : milestone.description_en}
          </div>
        </div>
        {stateStyle.icon}
      </div>

      {/* Metric progress bar */}
      {!isCompleted && target > 0 && (
        <div style={{ marginBottom: 12 }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            fontSize: 11,
            color: theme.textTertiary,
            marginBottom: 4
          }}>
            <span>{current.toFixed(1)}</span>
            <span>{t.goalOf} {target.toFixed(1)}</span>
          </div>
          <div style={{
            height: 6,
            borderRadius: 3,
            background: theme.bgInput,
            overflow: 'hidden'
          }}>
            <div style={{
              height: '100%',
              width: `${metricProgressPct}%`,
              background: metricProgressPct >= 100
                ? `linear-gradient(90deg, ${theme.success}, ${theme.primary})`
                : `linear-gradient(90deg, ${theme.primary}, ${theme.info})`,
              borderRadius: 3,
              transition: 'width 1s ease-out'
            }} />
          </div>
        </div>
      )}

      {/* Consecutive months streak */}
      {!isCompleted && required > 0 && (
        <div style={{ marginBottom: 12 }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            fontSize: 11,
            marginBottom: 4
          }}>
            <Flame size={11} color={achieved > 0 ? theme.warning : theme.textTertiary} />
            <span style={{ color: theme.textSecondary, fontWeight: 600 }}>
              {lang === 'ar' ? 'سلسلة الشهور' : 'Consecutive months'}
            </span>
            <span style={{
              marginLeft: 'auto',
              color: achieved >= required ? theme.success : theme.warning,
              fontWeight: 700
            }}>
              {achieved} / {required}
            </span>
          </div>
          <div style={{
            display: 'flex',
            gap: 3
          }}>
            {Array.from({ length: required }).map((_, i) => (
              <div
                key={i}
                style={{
                  flex: 1,
                  height: 4,
                  borderRadius: 2,
                  background: i < achieved ? theme.warning : theme.bgInput,
                  transition: 'background 0.3s'
                }}
              />
            ))}
          </div>
        </div>
      )}

      {/* Gate reason badge */}
      {(isLocked || isInProgress) && (
        <div style={{
          padding: '8px 10px',
          background: isLocked ? theme.bgInput : theme.warningDim,
          borderRadius: 6,
          fontSize: 11,
          color: isLocked ? theme.textSecondary : theme.warning,
          marginBottom: 12,
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          lineHeight: 1.4
        }}>
          {isLocked ? <Lock size={12} /> : <Clock size={12} />}
          <span>{lang === 'ar' ? gate.reason_ar : gate.reason_en}</span>
        </div>
      )}

      {/* Action area */}
      {isCompleted ? (
        <Badge variant="success">
          <Check size={11} />
          {milestone.manually_marked ? t.markManually : t.autoDetected}
        </Badge>
      ) : isReady ? (
        <Button
          size="sm"
          fullWidth
          onClick={() => onMarkComplete?.(milestone)}
        >
          <Check size={12} /> {t.markManually}
        </Button>
      ) : (
        <Button
          size="sm"
          variant="ghost"
          fullWidth
          disabled
          style={{ cursor: 'not-allowed' }}
        >
          <Lock size={12} /> {t.locked}
        </Button>
      )}
    </Card>
  );
}
